// PRD.305 — Laboratory Workflow & Patient Labs: pure domain logic.
//
// Everything here is dependency-free (no workspace-package imports) so it runs
// under the node:test harness and is shared verbatim by the Laboratory module,
// Patient Workspace Labs, Consultation Labs and the Files lock.
//
// Three ideas carry the whole model:
//
//   1. STATUS LIVES ON THE TEST. A lab order groups tests that progress
//      independently, so the order has no status column at all — its display
//      status is derived here (`aggregateLabOrderStatus`) and can never drift.
//
//   2. PUBLICATION LIVES ON THE RESULT, not on the uploaded file. MVP results
//      are file uploads, but structured Forms are a planned future source; a
//      Forms-backed result publishes through the identical guards with zero
//      artifacts attached.
//
//   3. PUBLISHED IS IMMUTABLE. Corrections append a new result that supersedes
//      the old one. Nothing published is ever edited or deleted.
//
// The repository has no state-machine framework and none is introduced here.
// The convention followed is the one used by delivery-order-logic.ts and
// patient-packages-logic.ts: pure `canX(...) => { ok, reason }` predicates that
// a service layer consults before issuing a direct UPDATE.

// ─── Per-test status (PRD §5.1) ───

export type LabTestStatus =
  | 'ordered'
  | 'collected'
  | 'in_progress'
  | 'result_ready'
  | 'published'
  | 'cancelled';

/** Canonical workflow order. `cancelled` is terminal and off-ladder. */
export const LAB_TEST_STATUSES: readonly LabTestStatus[] = [
  'ordered',
  'collected',
  'in_progress',
  'result_ready',
  'published',
  'cancelled',
] as const;

export const LAB_TEST_STATUS_LABELS: Record<LabTestStatus, string> = {
  ordered: 'Ordered',
  collected: 'Collected',
  in_progress: 'In Progress',
  result_ready: 'Result Ready',
  published: 'Published',
  cancelled: 'Cancelled',
};

/**
 * Position on the processing ladder. `cancelled` is deliberately absent — it is
 * not "further along", it is out of the flow, and every aggregation below
 * excludes it before ranking.
 */
const LAB_TEST_STATUS_RANK: Record<Exclude<LabTestStatus, 'cancelled'>, number> = {
  ordered: 0,
  collected: 1,
  in_progress: 2,
  result_ready: 3,
  published: 4,
};

/** Rank of a non-cancelled status (0..4). */
export function labTestStatusRank(status: LabTestStatus): number {
  if (status === 'cancelled') return -1;
  return LAB_TEST_STATUS_RANK[status];
}

/**
 * Coerce any stored/untrusted value to a valid status. Unknown, null and
 * legacy values read as `ordered` — the safest interpretation, since it is the
 * least-advanced state and therefore never claims work that has not happened.
 */
export function normalizeLabTestStatus(value: unknown): LabTestStatus {
  return typeof value === 'string' && (LAB_TEST_STATUSES as readonly string[]).includes(value)
    ? (value as LabTestStatus)
    : 'ordered';
}

/** The minimal shape aggregation needs. Real rows satisfy it structurally. */
export interface LabTestStatusSource {
  status?: string | null;
}

// ─── Aggregate order status (PRD §5.2) ───

/**
 * Derive an order's display status from its tests. DERIVED, never stored:
 * PRD §5.2 forbids a second independently editable aggregate that could drift.
 *
 * One rule does all of it:
 *
 *   no tests                 → 'ordered'
 *   every test cancelled     → 'cancelled'
 *   otherwise                → the LEAST-ADVANCED non-cancelled test's status
 *
 * That single rule reproduces each of the PRD's four requirements:
 *
 *   - all cancelled → Cancelled                       (explicit branch)
 *   - all published/cancelled, >=1 published →
 *     Published                                       (min over non-cancelled = published)
 *   - any result ready while none remain earlier in
 *     processing → Result Ready                       (min = result_ready)
 *   - otherwise the furthest meaningful incomplete
 *     state WITHOUT hiding tests still behind         (min = the laggard)
 *
 * Total, deterministic and order-independent: shuffling the input cannot change
 * the answer, because `min` is commutative.
 */
export function aggregateLabOrderStatus(tests: readonly LabTestStatusSource[]): LabTestStatus {
  if (tests.length === 0) return 'ordered';

  const active = tests
    .map((t) => normalizeLabTestStatus(t.status))
    .filter((s) => s !== 'cancelled') as Exclude<LabTestStatus, 'cancelled'>[];

  if (active.length === 0) return 'cancelled';

  return active.reduce((lowest, s) =>
    labTestStatusRank(s) < labTestStatusRank(lowest) ? s : lowest,
  );
}

/**
 * Whether an order still needs laboratory attention — i.e. its aggregate is
 * neither fully published nor fully cancelled. Drives the Laboratory queue's
 * default "open work" filter.
 */
export function isLabOrderOpen(tests: readonly LabTestStatusSource[]): boolean {
  const aggregate = aggregateLabOrderStatus(tests);
  return aggregate !== 'published' && aggregate !== 'cancelled';
}

// ─── Guards ───

/** Guard result, matching delivery-order-logic.ts / patient-packages-logic.ts. */
export interface LabGuardResult {
  ok: boolean;
  reason?: string;
}

const OK: LabGuardResult = { ok: true };
const deny = (reason: string): LabGuardResult => ({ ok: false, reason });

/**
 * Advance a test one or more steps along the ladder.
 *
 * Forward-only: a test never moves backwards, out of `cancelled`, or out of
 * `published`. `published` is reached only through `publishLabResult`, never by
 * a plain status advance — publication is an explicit act on a result, and
 * routing it through here would let a test claim published state with no
 * result behind it.
 */
export function canAdvanceLabTest(test: LabTestStatusSource, to: LabTestStatus): LabGuardResult {
  const from = normalizeLabTestStatus(test.status);
  if (from === 'cancelled') return deny('This test was cancelled.');
  if (from === 'published') return deny('This test is published and can no longer change.');
  if (to === 'cancelled') return deny('Use cancelLabTest to cancel a test.');
  if (to === 'published') return deny('Publish a result to move a test to Published.');
  if (labTestStatusRank(to) <= labTestStatusRank(from)) {
    return deny(`A test cannot move back from ${LAB_TEST_STATUS_LABELS[from]}.`);
  }
  return OK;
}

/** Cancel an ordered test. Published work is a clinical record and stays. */
export function canCancelLabTest(test: LabTestStatusSource): LabGuardResult {
  const status = normalizeLabTestStatus(test.status);
  if (status === 'published') return deny('A published test cannot be cancelled.');
  if (status === 'cancelled') return deny('This test is already cancelled.');
  return OK;
}

/** Attach a result artifact. Allowed at any live stage; blocked once terminal. */
export function canUploadLabResult(test: LabTestStatusSource): LabGuardResult {
  const status = normalizeLabTestStatus(test.status);
  if (status === 'cancelled') return deny('This test was cancelled.');
  if (status === 'published') return deny('This test is published — upload a correction instead.');
  return OK;
}

/** The minimal `lab_results` shape the guards need. */
export interface LabResultSource {
  published_at?: string | null;
  superseded_at?: string | null;
  result_ready_at?: string | null;
}

export function isLabResultPublished(result: LabResultSource): boolean {
  return result.published_at != null;
}

export function isLabResultSuperseded(result: LabResultSource): boolean {
  return result.superseded_at != null;
}

/**
 * Publish a result (PRD §6.2). Explicit, separate from upload, and requires:
 *   - a live test,
 *   - the test marked Result Ready (upload alone is not readiness),
 *   - a result that is not already published,
 *   - something to publish: at least one artifact, OR a non-file result source
 *     (`form` / `manual`), so the future Forms path needs no new guard.
 */
export function canPublishLabResult(input: {
  test: LabTestStatusSource;
  result: LabResultSource & { source?: string | null };
  attachedFileCount: number;
}): LabGuardResult {
  const status = normalizeLabTestStatus(input.test.status);
  if (status === 'cancelled') return deny('This test was cancelled.');
  if (isLabResultPublished(input.result)) return deny('This result is already published.');
  if (status !== 'result_ready') {
    return deny('Mark the test Result Ready before publishing.');
  }
  const source = input.result.source ?? 'file_upload';
  if (source === 'file_upload' && input.attachedFileCount < 1) {
    return deny('Attach at least one result file before publishing.');
  }
  return OK;
}

/**
 * PRD §6.3 — a published result is never replaced or edited in place. Always
 * denies; exists so callers express the rule by name rather than by comment.
 */
export function canReplacePublishedResult(): LabGuardResult {
  return deny('Published results are immutable. Publish a correction instead.');
}

/**
 * Issue a correction. Only a published, not-yet-superseded result can be
 * corrected — correcting a draft means editing it, and correcting an already
 * superseded result would fork the chain.
 */
export function canCorrectLabResult(result: LabResultSource): LabGuardResult {
  if (!isLabResultPublished(result)) return deny('Only a published result can be corrected.');
  if (isLabResultSuperseded(result)) {
    return deny('This result has already been corrected by a newer result.');
  }
  return OK;
}

/** Remove an artifact. Permitted only while its result is still a draft. */
export function canDeleteLabResultFile(result: LabResultSource): LabGuardResult {
  if (isLabResultPublished(result)) {
    return deny('This file belongs to a published result and cannot be removed.');
  }
  return OK;
}

// ─── Patient Workspace access modes (PRD §8.1, §12) ───

export type LaboratoryPatientWorkspaceAccess =
  | 'full_management'
  | 'monitor_results'
  | 'published_only';

export const LABORATORY_ACCESS_MODES: readonly LaboratoryPatientWorkspaceAccess[] = [
  'full_management',
  'monitor_results',
  'published_only',
] as const;

/** Exact PRD labels. */
export const LABORATORY_ACCESS_MODE_LABELS: Record<LaboratoryPatientWorkspaceAccess, string> = {
  full_management: 'Full Management',
  monitor_results: 'Monitor + Results',
  published_only: 'Published Results Only',
};

export const LABORATORY_ACCESS_MODE_DESCRIPTIONS: Record<LaboratoryPatientWorkspaceAccess, string> =
  {
    full_management:
      'Small clinics with no dedicated laboratory station. Patient Workspace can create orders, advance status, upload and publish results — always subject to permissions.',
    monitor_results:
      'Recommended for hybrid clinics. Patient Workspace monitors status and reads results; ordering and all operational work stay in the Laboratory module.',
    published_only:
      'Patient Workspace shows published results only, with no in-progress laboratory workflow.',
  };

/** PRD §12 / §20 — the backward-compatible default for every existing clinic. */
export const DEFAULT_LABORATORY_ACCESS: LaboratoryPatientWorkspaceAccess = 'monitor_results';

/** RBAC inputs, already resolved by the caller from the permission set. */
export interface LaboratoryPermissions {
  /** `Laboratory | Lab Orders` >= view */
  viewOrders: boolean;
  /** `Laboratory | Lab Orders` >= edit */
  createOrders: boolean;
  /** `Laboratory | Lab Orders` = full */
  manageOrders: boolean;
  /** `Laboratory | Lab Results` >= view */
  viewResults: boolean;
  /** `Laboratory | Lab Results` >= edit */
  manageWorkflow: boolean;
  /** `Laboratory | Lab Results` = full */
  publishResults: boolean;
}

/** Every capability a laboratory surface can offer. */
export interface LaboratoryCapabilities {
  /** See published results and open their files. */
  viewPublished: boolean;
  /** See in-progress orders/tests and their workflow state. */
  viewOperational: boolean;
  createOrder: boolean;
  cancelOrder: boolean;
  advanceStatus: boolean;
  uploadResult: boolean;
  publishResult: boolean;
}

const NO_CAPABILITIES: LaboratoryCapabilities = {
  viewPublished: false,
  viewOperational: false,
  createOrder: false,
  cancelOrder: false,
  advanceStatus: false,
  uploadResult: false,
  publishResult: false,
};

export type LaboratorySurface = 'laboratory' | 'patient_workspace' | 'consultation';

/**
 * The single resolver for "what may this user do with laboratory work HERE?".
 *
 * PRD §8.2 — configuration is not authorization. Effective access is the
 * INTERSECTION of module availability, the Laboratory Settings mode, and RBAC.
 * Selecting Full Management can only ever remove restrictions the mode itself
 * imposed; it can never hand a user a permission they do not hold, which is why
 * every operational capability below is ANDed with its permission flag.
 *
 * PRD.305 D5 — when the clinic disables the Laboratory main module, only
 * OPERATIONAL surfaces go away. Clinical history does not: the clinical
 * surfaces fall back to `published_only` rather than to nothing, so previously
 * published results stay readable in Patient Workspace, Consultation and Files.
 */
export function resolveLaboratoryCapabilities(input: {
  surface: LaboratorySurface;
  moduleEnabled: boolean;
  mode: LaboratoryPatientWorkspaceAccess;
  permissions: LaboratoryPermissions;
}): LaboratoryCapabilities {
  const { surface, moduleEnabled, permissions: p } = input;

  // The dedicated module IS the operational surface — it disappears entirely
  // with the module and is not shaped by the Patient Workspace access mode.
  if (surface === 'laboratory') {
    if (!moduleEnabled) return NO_CAPABILITIES;
    return {
      viewPublished: p.viewResults,
      viewOperational: p.viewOrders,
      createOrder: p.createOrders,
      cancelOrder: p.manageOrders,
      advanceStatus: p.manageWorkflow,
      uploadResult: p.manageWorkflow,
      publishResult: p.publishResults,
    };
  }

  // Clinical surfaces: a disabled module degrades to published-history-only.
  const mode: LaboratoryPatientWorkspaceAccess = moduleEnabled ? input.mode : 'published_only';

  if (mode === 'published_only') {
    return { ...NO_CAPABILITIES, viewPublished: p.viewResults };
  }

  if (mode === 'monitor_results') {
    // MONITOR AND READ ONLY. The mode means what its name says: the chart
    // watches laboratory work and reads results, and every act that CHANGES
    // laboratory work — including raising an order — belongs to the Laboratory
    // module. A clinic that wants clinicians ordering from the chart chooses
    // Full Management; that choice is the clinic's, not this function's.
    return {
      ...NO_CAPABILITIES,
      viewPublished: p.viewResults,
      viewOperational: p.viewOrders,
    };
  }

  // full_management — every operation still gated on its own permission.
  return {
    viewPublished: p.viewResults,
    viewOperational: p.viewOrders,
    createOrder: p.createOrders,
    cancelOrder: p.manageOrders,
    advanceStatus: p.manageWorkflow,
    uploadResult: p.manageWorkflow,
    publishResult: p.publishResults,
  };
}

/** True if a surface has nothing at all to show and should be hidden. */
export function isLaboratorySurfaceEmpty(caps: LaboratoryCapabilities): boolean {
  return !caps.viewPublished && !caps.viewOperational;
}

// ─── Catalog classification (PRD §11) ───

/**
 * Category names that HINT a service is a laboratory test.
 *
 * Read this carefully, because the distinction is the whole point:
 *
 *   `appointment_services.is_lab_test` is the CANONICAL classification.
 *   Nothing in the app resolves the laboratory catalog by category —
 *   `qLabTestCatalog` filters on the flag alone.
 *
 * This list exists ONLY to pre-fill the toggle in the Manage Services drawer
 * when someone picks the seeded "Lab Work" category, so the common case needs
 * one click instead of two. A clinic may rename or delete that category at any
 * time; if it does, this stops offering a suggestion and NOTHING about already
 * saved services changes.
 */
const LAB_TEST_CATEGORY_HINTS: readonly string[] = ['lab work', 'laboratory', 'labs', 'lab'];

/** Whether a category name hints at laboratory work. Convenience only. */
export function isLabTestCategoryName(name: string | null | undefined): boolean {
  if (!name) return false;
  return LAB_TEST_CATEGORY_HINTS.includes(name.trim().toLowerCase());
}

/**
 * Resolve what the "Laboratory Test" toggle should show as the user edits a
 * service. Pure, so the rule that protects saved data is unit-tested rather
 * than trusted to an effect in a drawer.
 *
 * Two invariants matter more than the convenience:
 *
 *   1. EDITING NEVER AUTO-CHANGES A SAVED VALUE. Re-categorising, renaming or
 *      re-pricing an existing service leaves `is_lab_test` exactly as stored.
 *      A service explicitly marked as a lab test does not silently stop being
 *      one because someone moved it to a different category.
 *
 *   2. AN EXPLICIT CHOICE WINS. Once the user has touched the toggle on a new
 *      service, category changes stop overriding them.
 *
 * Only an untouched toggle on a NEW service follows the category.
 */
export function resolveLabTestSuggestion(input: {
  /** 'edit' means an existing, already-persisted service. */
  mode: 'create' | 'edit';
  /** Name of the currently selected category, if any. */
  categoryName: string | null | undefined;
  /** Whether the user has explicitly set the toggle in this session. */
  userTouched: boolean;
  /** The toggle's current value. */
  current: boolean;
}): boolean {
  if (input.mode === 'edit') return input.current; // invariant 1
  if (input.userTouched) return input.current; // invariant 2
  return isLabTestCategoryName(input.categoryName);
}

// ─── Ordering drafts (PRD §11, walk-in + clinical) ───

/** A test being ordered — what `createLabOrder` persists per row. */
export interface LabOrderTestDraft {
  /** `appointment_services.id` when picked from the catalog; null for free-text. */
  serviceId: string | null;
  name: string;
  /** Catalog price at ordering time, in integer cents. */
  priceCents: number | null;
  code?: string | null;
}

/** The catalog fields selection needs. `LabTestCatalogRow` satisfies this. */
export interface LabCatalogEntry {
  id: string;
  name?: string | null;
  price_cents?: number | null;
  /** Present when reading raw `appointment_services` rows (0/1). */
  is_lab_test?: number | null;
  active?: number | null;
}

/**
 * Whether a catalog row may be ordered as a laboratory test.
 *
 * `qLabTestCatalog` already filters in SQL; this is the same rule expressed
 * where it can be unit-tested and reused by any caller reading raw
 * `appointment_services` rows. Classification comes from `is_lab_test` alone —
 * never from the category (PRD.305 D1).
 *
 * A row that omits the columns entirely (already-filtered query output) is
 * accepted, so passing `qLabTestCatalog` results through this is a no-op rather
 * than a silent wipe.
 */
export function isSelectableLabTest(row: LabCatalogEntry): boolean {
  if (row.is_lab_test !== undefined && row.is_lab_test !== null && row.is_lab_test !== 1) {
    return false;
  }
  if (row.active !== undefined && row.active !== null && row.active !== 1) return false;
  return true;
}

/** Keep only rows orderable as laboratory tests. */
export function filterSelectableLabTests<T extends LabCatalogEntry>(rows: readonly T[]): T[] {
  return rows.filter(isSelectableLabTest);
}

/**
 * Turn a selection into the per-test drafts `createLabOrder` writes — one draft
 * per selected test, so N selected tests become N `lab_order_tests` rows under
 * ONE order.
 *
 * Catalog picks carry a price snapshot taken now, so later re-pricing never
 * rewrites an existing order's charge. Free-text one-offs carry no service and
 * no price; they are still real ordered tests, just not billable against a
 * catalog entry.
 *
 * Non-selectable ids are dropped rather than silently ordered — the guard
 * belongs here so every ordering surface inherits it.
 */
export function buildLabOrderTestDrafts(input: {
  catalog: readonly LabCatalogEntry[];
  selectedIds: readonly string[];
  freeTextNames?: readonly string[];
}): LabOrderTestDraft[] {
  const selectable = new Map(
    filterSelectableLabTests(input.catalog).map((row) => [row.id, row] as const),
  );
  const drafts: LabOrderTestDraft[] = [];

  for (const id of input.selectedIds) {
    const row = selectable.get(id);
    if (!row) continue; // unknown, inactive, or not a lab test
    drafts.push({
      serviceId: row.id,
      name: row.name ?? 'Laboratory test',
      priceCents: row.price_cents ?? null,
    });
  }

  for (const name of input.freeTextNames ?? []) {
    const trimmed = name.trim();
    if (trimmed) drafts.push({ serviceId: null, name: trimmed, priceCents: null });
  }

  return drafts;
}

/** Total of a draft selection, in integer cents. */
export function labOrderDraftTotalCents(drafts: readonly LabOrderTestDraft[]): number {
  return drafts.reduce((sum, d) => sum + (d.priceCents ?? 0), 0);
}

/** The encounter fields the attach picker needs. */
export interface LabOrderEncounterCandidate {
  id: string;
  visit_id?: string | null;
  status?: string | null;
  started_at?: string | null;
}

/** Where a new order should be anchored. Both ids may be null (walk-in). */
export interface LabOrderAnchor {
  encounterId: string | null;
  visitId: string | null;
}

const NO_ANCHOR: LabOrderAnchor = { encounterId: null, visitId: null };

/**
 * Resolve which consultation (if any) a new order attaches to.
 *
 * A walk-in laboratory order is a first-class case, so this NEVER invents an
 * anchor: with no open encounter it returns nulls and the order is created
 * unattached. `lab_orders.encounter_id` / `visit_id` are nullable precisely so
 * that path needs no placeholder visit.
 *
 * `explicitId` is the user's choice from the attach picker; `null` means
 * "don't attach" and is honoured even when an open encounter exists.
 */
export function resolveLabOrderAnchor(
  candidates: readonly LabOrderEncounterCandidate[],
  explicitId?: string | null,
): LabOrderAnchor {
  if (explicitId) {
    const chosen = candidates.find((c) => c.id === explicitId);
    return chosen ? { encounterId: chosen.id, visitId: chosen.visit_id ?? null } : NO_ANCHOR;
  }
  if (explicitId === null) return NO_ANCHOR; // explicit "no consultation"
  const open = candidates.find((c) => c.status === 'open');
  return open ? { encounterId: open.id, visitId: open.visit_id ?? null } : NO_ANCHOR;
}

// ─── Flow steps + KPI summary (PRD.305 — Laboratory Flow) ───

/**
 * The progression strip shown on a Laboratory Flow row.
 *
 * `cancelled` is deliberately absent: it is not a step along the way, it is an
 * exit from the ladder. Cancelled work is reachable through its own filter.
 */
export const LAB_FLOW_STEPS: readonly Exclude<LabTestStatus, 'cancelled'>[] = [
  'ordered',
  'collected',
  'in_progress',
  'result_ready',
  'published',
] as const;

export type LabFlowStep = (typeof LAB_FLOW_STEPS)[number];
export type LabFlowStepVariant = 'completed' | 'next' | 'future';

/**
 * Where a step sits relative to a test's current status. Mirrors Queue's
 * `stepVariant` contract exactly, so the shared chevron strip reads the same
 * in both modules: completed = already fired, next = the obvious click,
 * future = still ahead.
 */
export function labFlowStepVariant(
  currentStatus: string | null | undefined,
  step: LabFlowStep,
): LabFlowStepVariant {
  const current = normalizeLabTestStatus(currentStatus);
  if (current === 'cancelled') return 'future';
  const cur = labTestStatusRank(current);
  const target = labTestStatusRank(step);
  if (cur >= target) return 'completed';
  if (cur + 1 === target) return 'next';
  return 'future';
}

/**
 * Marking a test Result Ready requires a result to actually exist.
 *
 * Upload creates a `lab_results` row, so `resultCount` covers today's manual
 * uploads AND a future Forms-sourced result without this guard needing to know
 * which. Without it, a test could claim readiness with nothing behind it and
 * then fail at the publish step for a reason the user never saw coming.
 */
export function canMarkLabTestResultReady(input: {
  test: LabTestStatusSource;
  resultCount: number;
}): LabGuardResult {
  const advance = canAdvanceLabTest(input.test, 'result_ready');
  if (!advance.ok) return advance;
  if (input.resultCount < 1) {
    return deny('Upload a result before marking this test Result Ready.');
  }
  return OK;
}

/** Per-status order counts for the Flow KPI row. */
export interface LabFlowSummary {
  /** In-house work that is neither fully published nor fully cancelled. */
  totalOpen: number;
  ordered: number;
  collected: number;
  in_progress: number;
  result_ready: number;
  published: number;
  cancelled: number;
}

/** The order shape the summary needs — `LabOrderRow` satisfies it. */
export interface LabFlowSummarySource extends LabFulfillmentSource, LabOrderVisibilitySource {}

/**
 * Count orders per derived aggregate status, plus the open-work total.
 *
 * Each figure is computed with the SAME predicate its filter uses, so a card
 * and the list it describes can never disagree. `totalOpen` excludes Published
 * and Cancelled (and anything not in-house), matching the Open Work filter.
 */
export function summarizeLabFlow(orders: readonly LabFlowSummarySource[]): LabFlowSummary {
  const summary: LabFlowSummary = {
    totalOpen: 0,
    ordered: 0,
    collected: 0,
    in_progress: 0,
    result_ready: 0,
    published: 0,
    cancelled: 0,
  };
  for (const order of orders) {
    const tests = parseTestStatusList(order.test_statuses);
    summary[aggregateLabOrderStatus(tests)] += 1;
    if (isQueuedLabWork(order, tests)) summary.totalOpen += 1;
  }
  return summary;
}

// ─── Lab Work Forms (PRD.305 follow-up) ───

/** Deterministic id for a (service, form) assignment, so retries converge. */
export function laboratoryServiceFormId(serviceId: string, formDefinitionId: string): string {
  return `lsf-${serviceId}-${formDefinitionId}`;
}

/** The service fields the builder's Laboratory Services picker needs. */
export interface LabFormServiceOption {
  id: string;
  name?: string | null;
  is_lab_test?: number | null;
  active?: number | null;
}

/**
 * Services selectable as a Lab Work form's Laboratory Services.
 *
 * Eligibility is `appointment_services.is_lab_test` and nothing else — never a
 * category name or id, which are user-editable. Reuses the same predicate the
 * ordering catalog uses so the builder and the order drawer can never disagree
 * about what counts as a laboratory test.
 */
export function selectableLabFormServices<T extends LabFormServiceOption>(
  services: readonly T[],
  search = '',
): T[] {
  const q = search.trim().toLowerCase();
  return filterSelectableLabTests(services).filter(
    (s) => !q || (s.name ?? '').toLowerCase().includes(q),
  );
}

/** A form definition as the Result Ready drawer sees it. */
export interface LabWorkFormSource {
  id: string;
  status?: string | null;
  purpose?: string | null;
}

/**
 * Forms a technician may actually fill in for a result.
 *
 * Only PUBLISHED Lab Work definitions qualify: publishing a form definition is
 * what makes it usable for data entry. That is a Forms-lifecycle event and has
 * nothing to do with publishing a patient's laboratory result — the two
 * lifecycles are independent (PRD §4).
 */
export function usableLabWorkForms<T extends LabWorkFormSource>(forms: readonly T[]): T[] {
  return forms.filter(
    (f) => f.status === 'published' && (f.purpose === undefined || f.purpose === 'lab_work'),
  );
}

/** What a result save is carrying. */
export interface LabResultContent {
  /** `form_instances.id` values to link. */
  formInstanceIds?: readonly string[];
  /** Uploaded artifacts to link. */
  fileCount?: number;
  /** Artifacts already saved on the result being appended to. */
  existingFileCount?: number;
  /** Forms already linked to the result being appended to. */
  existingFormCount?: number;
}

/**
 * Whether a Result Ready save has anything to record.
 *
 * A structured form, an uploaded report, or both — any one is enough, and
 * completing every assigned form is never required. A save carrying nothing
 * new AND landing on an empty result is refused, because that would advance a
 * test to Result Ready with no result behind it.
 */
export function canSaveLabResult(content: LabResultContent): LabGuardResult {
  const incoming = (content.formInstanceIds?.length ?? 0) + (content.fileCount ?? 0);
  const existing = (content.existingFormCount ?? 0) + (content.existingFileCount ?? 0);
  if (incoming + existing < 1) {
    return deny('Add a completed result form or upload a report before saving.');
  }
  return OK;
}

/**
 * How the Result Ready drawer should present the assigned forms (PRD §5).
 *
 *   none     → Upload Report only
 *   single   → that form presented directly, Upload Report retained
 *   multiple → pick whichever apply, Upload Report retained
 *
 * Upload Report is available in every mode, so the original file-only workflow
 * never regresses.
 */
export type LabResultFormsPresentation = 'none' | 'single' | 'multiple';

export function labResultFormsPresentation(
  forms: readonly LabWorkFormSource[],
): LabResultFormsPresentation {
  const usable = usableLabWorkForms(forms);
  if (usable.length === 0) return 'none';
  return usable.length === 1 ? 'single' : 'multiple';
}

// ─── Payment: a Flow step, never a test status (PRD.305) ───
//
// Pay sits in the Flow strip between Ordered and Collected, but it is NOT a
// laboratory status and must never become one. `lab_order_tests.status` stays
// exactly the six clinical values; payment is DERIVED from the existing
// invoice/invoice-item settlement the billing subsystem already owns.
//
// The two axes are independent by construction: nothing here writes a status,
// and no status transition consults or changes settlement. An unpaid order can
// still be collected and processed — this PRD introduces no payment gate.

export type LabPaymentState = 'unbilled' | 'unpaid' | 'partial' | 'paid';

export const LAB_PAYMENT_LABELS: Record<LabPaymentState, string> = {
  unbilled: 'Pay',
  unpaid: 'Pay',
  partial: 'Partial',
  paid: 'Paid',
};

/** The per-test billing linkage `qLabOrderTests` already returns. */
export interface LabTestPaymentSource {
  status?: string | null;
  /** `invoice_items.payment_status` for this test's line, if it has one. */
  invoice_payment_status?: string | null;
  invoice_id?: string | null;
}

export interface LabOrderPayment {
  state: LabPaymentState;
  /** The invoice the Pay step opens. Null when nothing has been billed. */
  invoiceId: string | null;
  billedCount: number;
  paidCount: number;
}

/**
 * Derive an order's payment state from its tests' invoice lines.
 *
 * Payment is an ORDER/INVOICE-level concern even though laboratory status is
 * per-test: the tests of one order are billed onto one invoice, so the strip
 * shows a single Pay segment while each test keeps its own clinical status.
 *
 * Cancelled tests are ignored — they are not billable, so an order whose live
 * tests are all settled reads Paid rather than being dragged back to Partial by
 * a cancelled sibling.
 */
export function summarizeLabOrderPayment(tests: readonly LabTestPaymentSource[]): LabOrderPayment {
  const billable = tests.filter((t) => normalizeLabTestStatus(t.status) !== 'cancelled');
  const billed = billable.filter((t) => t.invoice_payment_status != null);
  const paid = billed.filter((t) => t.invoice_payment_status === 'paid');
  const invoiceId = billed.find((t) => t.invoice_id)?.invoice_id ?? null;

  if (billed.length === 0) {
    return { state: 'unbilled', invoiceId: null, billedCount: 0, paidCount: 0 };
  }

  const result = { invoiceId, billedCount: billed.length, paidCount: paid.length };

  // Every billable test settled — and nothing left unbilled.
  if (paid.length === billed.length && billed.length === billable.length) {
    return { ...result, state: 'paid' };
  }
  // Something has been settled, or a line reports a partial settlement.
  if (paid.length > 0 || billed.some((t) => t.invoice_payment_status === 'partial')) {
    return { ...result, state: 'partial' };
  }
  return { ...result, state: 'unpaid' };
}

/** Whether the Pay step can open a bill. Unbilled work has nothing to open. */
export function canOpenLabPayment(payment: LabOrderPayment): boolean {
  return payment.invoiceId != null;
}

/**
 * Flow-strip variant for the Pay step, mirroring Queue's contract:
 *   paid    → completed (green), driven by real settlement
 *   partial /
 *   unpaid  → next (primary), always actionable
 *   unbilled→ future (muted), nothing to settle yet
 */
export function labPaymentStepVariant(payment: LabOrderPayment): LabFlowStepVariant {
  if (payment.state === 'paid') return 'completed';
  if (payment.state === 'unbilled') return 'future';
  return 'next';
}

// ─── Fulfillment: who actually performs the work (PRD.305) ───
//
// A clinician's lab request is a RECOMMENDATION. The patient may take it to any
// laboratory, so raising it during a consultation must not conscript the
// clinic into doing the work. Fulfillment is therefore separate from the test
// status ladder: a request sits outside the Flow work queue until someone
// accepts it in-house or records that it was done elsewhere.

export type LabFulfillment = 'requested' | 'in_house' | 'external';

export const LAB_FULFILLMENTS: readonly LabFulfillment[] = [
  'requested',
  'in_house',
  'external',
] as const;

export const LAB_FULFILLMENT_LABELS: Record<LabFulfillment, string> = {
  requested: 'Requested',
  in_house: 'In-house',
  external: 'Done externally',
};

/**
 * Coerce a stored/untrusted value. Anything unrecognised — including the NULL
 * every order carried before this column existed — reads as `in_house`, which
 * is what those orders already were: live laboratory work.
 */
export function normalizeLabFulfillment(value: unknown): LabFulfillment {
  return typeof value === 'string' && (LAB_FULFILLMENTS as readonly string[]).includes(value)
    ? (value as LabFulfillment)
    : 'in_house';
}

/** The order fields fulfillment logic needs. `LabOrderRow` satisfies this. */
export interface LabFulfillmentSource {
  fulfillment?: string | null;
}

/** A clinician's request nobody has claimed or dismissed yet. */
export function isPendingLabRequest(order: LabFulfillmentSource): boolean {
  return normalizeLabFulfillment(order.fulfillment) === 'requested';
}

/** Work the clinic is actually performing — the only thing Flow queues. */
export function isInHouseLabWork(order: LabFulfillmentSource): boolean {
  return normalizeLabFulfillment(order.fulfillment) === 'in_house';
}

/**
 * The fulfillment a NEW order should start in.
 *
 * Only the consultation path defers: a doctor prescribing labs is recommending
 * them, not booking the clinic's bench. An order raised from the Laboratory
 * module or the patient's chart is staff putting work into the clinic, so it
 * starts in-house and behaves exactly as it did before this distinction
 * existed.
 */
export function initialLabFulfillment(
  source: 'consultation' | 'patient_workspace' | 'laboratory',
): LabFulfillment {
  return source === 'consultation' ? 'requested' : 'in_house';
}

/** Accept a request as in-house work. Only a pending request can be accepted. */
export function canAcceptLabRequest(order: LabFulfillmentSource): LabGuardResult {
  const fulfillment = normalizeLabFulfillment(order.fulfillment);
  if (fulfillment === 'in_house') return deny('This order is already in-house laboratory work.');
  if (fulfillment === 'external') {
    return deny('This request was recorded as done externally.');
  }
  return OK;
}

/**
 * Record that the patient had the work done elsewhere. Only a pending request
 * qualifies — once the clinic has started in-house work, saying it went
 * elsewhere would contradict the test statuses already recorded against it.
 */
export function canMarkLabRequestExternal(order: LabFulfillmentSource): LabGuardResult {
  const fulfillment = normalizeLabFulfillment(order.fulfillment);
  if (fulfillment === 'external') return deny('This request is already marked as done externally.');
  if (fulfillment === 'in_house') {
    return deny('This order is in-house laboratory work; cancel its tests instead.');
  }
  return OK;
}

/**
 * Whether an order belongs in the Laboratory Flow WORK queue.
 *
 * Pending requests and externally-done work are excluded: neither is work the
 * bench should be picking up. They remain visible under their own filter and in
 * the patient's chart — nothing is hidden, it is just not queued.
 */
export function isQueuedLabWork(
  order: LabFulfillmentSource,
  tests: readonly LabTestStatusSource[],
): boolean {
  return isInHouseLabWork(order) && isLabOrderOpen(tests);
}

// ─── Consultation ordering (PRD.305 — Prescriptions workspace) ───

/**
 * Parse the comma-joined `test_statuses` rollup a lab-order query returns into
 * aggregation input. Lives here so the SQL rollup and the aggregation rule stay
 * next to each other.
 */
export function parseTestStatusList(joined: string | null | undefined): { status: string }[] {
  if (!joined) return [];
  return joined
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((status) => ({ status }));
}

/** The order fields visibility selection needs. `LabOrderRow` satisfies this. */
export interface LabOrderVisibilitySource {
  test_statuses?: string | null;
}

/**
 * The orders a CLINICAL surface may show.
 *
 * Under `Published Results Only` — and whenever the Laboratory module is
 * switched off — a chart shows published work only, so in-progress laboratory
 * workflow never leaks into a surface that is not meant to see it. Shared so
 * the Prescription canvas and its Laboratory group can never disagree about
 * what exists: a canvas that thought there were no orders while the group
 * rendered some (or vice versa) is exactly how an "empty" state ends up sitting
 * above a populated list.
 */
export function selectVisibleLabOrders<T extends LabOrderVisibilitySource>(
  orders: readonly T[],
  viewOperational: boolean,
): T[] {
  if (viewOperational) return [...orders];
  return orders.filter(
    (o) => aggregateLabOrderStatus(parseTestStatusList(o.test_statuses)) === 'published',
  );
}

/**
 * Whether the Consultation ordering workspace should offer its
 * Medications | Laboratory switch.
 *
 * Shown when the user can see laboratory work at all — either operational
 * orders or published results. When they cannot, the switch disappears and the
 * workspace is exactly the pre-PRD.305 prescription surface, so laboratory
 * support is additive rather than a change everyone sees.
 *
 * Held back until permissions RESOLVE: permission checks fail closed while
 * loading, so deciding on an unresolved set would flash the switch away from
 * users who do have access.
 */
export function isLaboratoryViewAvailable(input: {
  permissionsReady: boolean;
  capabilities: Pick<LaboratoryCapabilities, 'viewOperational' | 'viewPublished'>;
}): boolean {
  if (!input.permissionsReady) return false;
  return input.capabilities.viewOperational || input.capabilities.viewPublished;
}

// ─── Lab request pickup (PRD.305 — New Lab Order, Pharmacy-parity) ───

/** Billing-progress counts for one lab order. `LabRequestRow` satisfies this. */
export interface LabRequestProgress {
  billable_tests?: number | null;
  invoiced_tests?: number | null;
  paid_tests?: number | null;
  ordered_at?: string | null;
}

/**
 * Whether a request still needs billing attention — mirrors Pharmacy's
 * prescription rule: it stays in the queue until every billable test is on a
 * PAID invoice, so a billed-but-unpaid order does not disappear before payment
 * is collected.
 */
export function isLabRequestEligible(row: LabRequestProgress): boolean {
  return Number(row.billable_tests ?? 0) > Number(row.paid_tests ?? 0);
}

/**
 * Short status hint for a request row, mirroring the Pharmacy queue's wording
 * so the two drawers read the same way.
 */
export function labRequestStatusHint(row: LabRequestProgress): string {
  const billable = Number(row.billable_tests ?? 0);
  const invoiced = Number(row.invoiced_tests ?? 0);
  const paid = Number(row.paid_tests ?? 0);
  const toBill = billable - invoiced;
  if (toBill > 0) return `${toBill} to bill`;
  if (invoiced - paid > 0) return 'Awaiting payment';
  if (billable > 0) return 'Fully invoiced';
  return 'No billable tests';
}

/**
 * Split a patient's requests into Today / Earlier (still unsettled) / Already
 * billed. Sectioning rather than filtering is deliberate: hiding settled orders
 * makes the drawer claim a patient has no laboratory work when they plainly do.
 */
export function sectionLabRequests<T extends LabRequestProgress>(
  rows: readonly T[],
  todayIso: string,
): { today: T[]; earlier: T[]; settled: T[] } {
  const today: T[] = [];
  const earlier: T[] = [];
  const settled: T[] = [];
  for (const row of rows) {
    if (!isLabRequestEligible(row)) settled.push(row);
    else if ((row.ordered_at ?? '').slice(0, 10) === todayIso) today.push(row);
    else earlier.push(row);
  }
  return { today, earlier, settled };
}

/**
 * The tests of a picked request that still need an invoice line — what the
 * "Add to Invoice" action bills. Cancelled tests are excluded, and anything
 * already invoiced is skipped so re-opening a partially billed request never
 * double-charges.
 */
export function unbilledLabTestIds(
  tests: readonly { id: string; status?: string | null; invoice_payment_status?: string | null }[],
): string[] {
  return tests
    .filter((t) => normalizeLabTestStatus(t.status) !== 'cancelled')
    .filter((t) => t.invoice_payment_status == null)
    .map((t) => t.id);
}

// ─── Billing (PRD §11) ───

/**
 * `invoice_items.source_type` for a laboratory test. Laboratory bills through
 * the EXISTING invoice path — there is no laboratory invoice or payment table.
 */
export const LAB_TEST_INVOICE_SOURCE_TYPE = 'lab_test';

/**
 * Deterministic `invoice_items.id` for a billed test, mirroring PRD.270's
 * walk-in import: two clients billing the same test (or an upload retry)
 * produce the SAME id and converge through `ON CONFLICT (id) DO UPDATE`, so a
 * duplicate line is impossible without a partial unique index.
 */
export function labInvoiceItemId(labOrderTestId: string): string {
  return `lab-${labOrderTestId}`;
}

/** The ordered-test fields billing needs. */
export interface LabTestBillingSource {
  id: string;
  service_id?: string | null;
  test_name?: string | null;
  price_cents_snapshot?: number | null;
  status?: string | null;
}

/** An invoice line ready to insert through the existing billing path. */
export interface LabInvoiceLineDraft {
  id: string;
  service_id: string | null;
  service_name: string;
  qty: number;
  unit_price: number;
  source_type: string;
  source_id: string;
}

/**
 * Map an ordered test onto an invoice line. Price comes from the snapshot taken
 * at ordering time (integer cents → major units), so re-pricing the catalog
 * later never rewrites an existing order's charge.
 */
export function toLabInvoiceLine(test: LabTestBillingSource): LabInvoiceLineDraft {
  const cents = Number(test.price_cents_snapshot ?? 0);
  return {
    id: labInvoiceItemId(test.id),
    service_id: test.service_id ?? null,
    service_name: test.test_name ?? 'Laboratory test',
    qty: 1,
    unit_price: Number.isFinite(cents) ? cents / 100 : 0,
    source_type: LAB_TEST_INVOICE_SOURCE_TYPE,
    source_id: test.id,
  };
}

/** A cancelled test is not billable; everything else is. */
export function isLabTestBillable(test: LabTestBillingSource): boolean {
  return normalizeLabTestStatus(test.status) !== 'cancelled';
}

// ─── Result artifacts (PRD §10) ───

/** Joined `lab_result_files` + `patient_files` row. */
export interface LabResultArtifactSource {
  id: string;
  lab_result_id?: string | null;
  patient_file_id?: string | null;
  filename?: string | null;
  url?: string | null;
  mime_type?: string | null;
  sort_order?: number | null;
  published_at?: string | null;
}

export interface LabResultArtifact {
  /** `lab_result_files.id` — the linkage row. */
  linkId: string;
  /** `patient_files.id` — THE artifact. Identical in Labs and in Files. */
  fileId: string;
  resultId: string | null;
  filename: string;
  url: string | null;
  mimeType: string | null;
  sortOrder: number;
  published: boolean;
}

/**
 * Normalize a joined row into the shape both Labs and Files render.
 *
 * `fileId` is deliberately `patient_files.id`, not the link id: opening a
 * document from Labs and from Files must resolve to the same stored object, so
 * there is exactly one physical upload no matter how many surfaces show it.
 * Rows with no linked file are dropped — a dangling link has nothing to open.
 */
export function toLabResultArtifact(row: LabResultArtifactSource): LabResultArtifact | null {
  if (!row.patient_file_id) return null;
  return {
    linkId: row.id,
    fileId: row.patient_file_id,
    resultId: row.lab_result_id ?? null,
    filename: row.filename ?? 'Result file',
    url: row.url ?? null,
    mimeType: row.mime_type ?? null,
    sortOrder: Number(row.sort_order ?? 0),
    published: row.published_at != null,
  };
}

/**
 * Split artifacts by publication so a surface can render "available to
 * clinicians" separately from "uploaded, awaiting publication" — the visual
 * distinction PRD §17 requires.
 */
export function partitionLabArtifacts(rows: readonly LabResultArtifactSource[]): {
  published: LabResultArtifact[];
  unpublished: LabResultArtifact[];
} {
  const published: LabResultArtifact[] = [];
  const unpublished: LabResultArtifact[] = [];
  for (const row of rows) {
    const artifact = toLabResultArtifact(row);
    if (!artifact) continue;
    (artifact.published ? published : unpublished).push(artifact);
  }
  const bySort = (a: LabResultArtifact, b: LabResultArtifact) => a.sortOrder - b.sortOrder;
  return { published: published.sort(bySort), unpublished: unpublished.sort(bySort) };
}

// ─── Visit / encounter scoping (PRD §9) ───

/** The order fields scoping needs. */
export interface LabOrderScopeSource {
  id: string;
  encounter_id?: string | null;
  visit_id?: string | null;
}

/**
 * Orders belonging to one consultation. Consultation Labs is visit-centric, so
 * an order raised from the Patient Workspace with no encounter (a very common
 * case) is correctly absent here while still appearing in Patient Labs.
 */
export function filterOrdersForEncounter<T extends LabOrderScopeSource>(
  orders: readonly T[],
  encounterId: string | null,
): T[] {
  if (!encounterId) return [];
  return orders.filter((o) => o.encounter_id === encounterId);
}

/** Orders belonging to a visit — broader than an encounter, used by summaries. */
export function filterOrdersForVisit<T extends LabOrderScopeSource>(
  orders: readonly T[],
  visitId: string | null,
): T[] {
  if (!visitId) return [];
  return orders.filter((o) => o.visit_id === visitId);
}
